import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/media")({
  
  head: () => ({ meta: [{ title: "Admin — Media | Mine Class" }, { name: "description", content: "Panel de administración de Media — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: MediaManager,
});

type Folder = { id: string; name: string; parent_id: string | null; created_at: string; display_date: string | null; sort_order: number };
type Asset = {
  id: string;
  folder_id: string | null;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  alt_text: string | null;
  caption: string | null;
  created_at: string;
};

const BUCKET = "media";

function MediaManager() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [signed, setSigned] = useState<Record<string, string>>({});
  const [current, setCurrent] = useState<string | null>(null); // folder id (null = root)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newFolder, setNewFolder] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const currentFolder = useMemo(() => folders.find((f) => f.id === current) ?? null, [folders, current]);
  const breadcrumb = useMemo(() => {
    const chain: Folder[] = [];
    let node = currentFolder;
    while (node) {
      chain.unshift(node);
      node = folders.find((f) => f.id === node!.parent_id) ?? null;
    }
    return chain;
  }, [currentFolder, folders]);

  const childFolders = folders.filter((f) => f.parent_id === current);
  const childAssets = assets.filter((a) => a.folder_id === current);

  async function load() {
    const [f, a] = await Promise.all([
      supabase.from("media_folders").select("*").order("sort_order", { ascending: true }).order("display_date", { ascending: false, nullsFirst: false }).order("created_at", { ascending: false }),
      supabase
        .from("media_assets")
        .select("id,folder_id,storage_path,file_name,mime_type,file_size_bytes,alt_text,caption,created_at")
        .order("created_at", { ascending: false })
        .limit(500),
    ]);
    setFolders((f.data ?? []) as Folder[]);
    const list = (a.data ?? []) as Asset[];
    setAssets(list);
    const map: Record<string, string> = {};
    await Promise.all(
      list.map(async (asset) => {
        const { data: s } = await supabase.storage.from(BUCKET).createSignedUrl(asset.storage_path, 3600);
        if (s?.signedUrl) map[asset.id] = s.signedUrl;
      }),
    );
    setSigned(map);
  }

  useEffect(() => {
    load();
  }, []);

  async function createFolder() {
    const name = newFolder.trim();
    if (!name) return;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const { error } = await supabase.from("media_folders").insert({ name, parent_id: current, created_by: userId });
    if (error) {
      setErr(error.message);
      return;
    }
    setNewFolder("");
    load();
  }

  async function deleteFolder(f: Folder) {
    if (!confirm(`Eliminar carpeta "${f.name}" y su contenido asociado?`)) return;
    await supabase.from("media_folders").delete().eq("id", f.id);
    load();
  }

  async function renameFolder(f: Folder) {
    const name = prompt("Nuevo nombre de la carpeta:", f.name)?.trim();
    if (!name || name === f.name) return;
    const { error } = await supabase.from("media_folders").update({ name }).eq("id", f.id);
    if (error) {
      setErr(error.message);
      return;
    }
    load();
  }

  async function updateFolderDate(f: Folder, date: string) {
    await supabase.from("media_folders").update({ display_date: date || null }).eq("id", f.id);
    load();
  }

  async function moveFolder(f: Folder, dir: -1 | 1) {
    const siblings = folders
      .filter((x) => x.parent_id === f.parent_id)
      .sort((a, b) => (a.sort_order - b.sort_order) || (b.display_date ?? b.created_at).localeCompare(a.display_date ?? a.created_at));
    const idx = siblings.findIndex((x) => x.id === f.id);
    const swap = siblings[idx + dir];
    if (!swap) return;
    // Normalize sort_order so swaps always work
    const updates = siblings.map((s, i) => ({ id: s.id, sort_order: i }));
    const a = updates.find((u) => u.id === f.id)!;
    const b = updates.find((u) => u.id === swap.id)!;
    [a.sort_order, b.sort_order] = [b.sort_order, a.sort_order];
    await Promise.all(updates.map((u) => supabase.from("media_folders").update({ sort_order: u.sort_order }).eq("id", u.id)));
    load();
  }

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const folderSlug = current ? `folder-${current}` : "root";
      const path = `${userId}/${folderSlug}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("media_assets").insert({
        folder_id: current,
        storage_path: path,
        file_name: file.name,
        mime_type: file.type,
        file_size_bytes: file.size,
        uploaded_by: userId,
      });
      if (insErr) throw insErr;
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo archivo");
    } finally {
      setBusy(false);
    }
  }

  async function remove(a: Asset) {
    if (!confirm(`Eliminar ${a.file_name}?`)) return;
    await supabase.storage.from(BUCKET).remove([a.storage_path]);
    await supabase.from("media_assets").delete().eq("id", a.id);
    load();
  }

  async function copyUrl(id: string) {
    const url = signed[id];
    if (!url) return;
    await navigator.clipboard.writeText(url);
    alert("URL copiada (válida por 1 hora).");
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <div className="text-sm flex flex-wrap items-center gap-1 text-[var(--color-on-surface-variant)]">
        <button onClick={() => setCurrent(null)} className="hover:underline font-semibold text-[var(--color-brand-purple)]">
          📁 Raíz
        </button>
        {breadcrumb.map((f) => (
          <span key={f.id} className="flex items-center gap-1">
            <span>/</span>
            <button onClick={() => setCurrent(f.id)} className="hover:underline">{f.name}</button>
          </span>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[var(--color-outline-variant)] p-4">
          <h3 className="font-bold text-sm mb-2">Nueva carpeta {currentFolder ? `en "${currentFolder.name}"` : "en raíz"}</h3>
          <div className="flex gap-2">
            <input
              placeholder="Ej: Campaña 2026, Logos, Cursos..."
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              className="flex-1 border border-[var(--color-outline-variant)] px-3 py-2 text-sm"
            />
            <button onClick={createFolder} className="px-4 py-2 bg-[var(--color-brand-purple)] text-white text-sm font-bold">
              Crear
            </button>
          </div>
        </div>
        <div className="bg-white border border-[var(--color-outline-variant)] p-4">
          <h3 className="font-bold text-sm mb-2">Subir archivo {currentFolder ? `a "${currentFolder.name}"` : "a raíz"}</h3>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={onUpload}
            disabled={busy}
            className="text-sm"
          />
          {busy && <p className="text-sm text-[var(--color-on-surface-variant)] mt-2">Subiendo...</p>}
        </div>
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}

      {/* Folders */}
      {childFolders.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-2">
            Carpetas (usa ▲▼ para reordenar y muestra la fecha)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {childFolders.map((f, i) => (
              <div key={f.id} className="bg-white border border-[var(--color-outline-variant)] p-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveFolder(f, -1)}
                      disabled={i === 0}
                      className="text-xs px-1 disabled:opacity-30 hover:text-[var(--color-brand-purple)]"
                      title="Subir"
                    >▲</button>
                    <button
                      onClick={() => moveFolder(f, 1)}
                      disabled={i === childFolders.length - 1}
                      className="text-xs px-1 disabled:opacity-30 hover:text-[var(--color-brand-purple)]"
                      title="Bajar"
                    >▼</button>
                  </div>
                  <button onClick={() => setCurrent(f.id)} className="flex items-center gap-2 text-left flex-1 truncate">
                    <span className="text-2xl">📁</span>
                    <span className="font-semibold truncate">{f.name}</span>
                  </button>
                  <button
                    onClick={() => renameFolder(f)}
                    className="text-xs text-[var(--color-brand-purple)] hover:underline"
                    title="Renombrar carpeta"
                  >
                    ✎
                  </button>
                  <button
                    onClick={() => deleteFolder(f)}
                    className="text-xs text-red-600 hover:underline"
                    title="Eliminar carpeta"
                  >
                    ✕
                  </button>
                </div>
                <label className="flex items-center gap-2 text-xs text-[var(--color-on-surface-variant)]">
                  Fecha visible:
                  <input
                    type="date"
                    defaultValue={f.display_date ?? ""}
                    onBlur={(e) => {
                      if ((e.target.value || null) !== f.display_date) updateFolderDate(f, e.target.value);
                    }}
                    className="border border-[var(--color-outline-variant)] px-2 py-1 text-xs"
                  />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assets */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-2">
          Archivos ({childAssets.length})
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {childAssets.map((a) => (
            <div key={a.id} className="bg-white border border-[var(--color-outline-variant)] overflow-hidden">
              <div className="aspect-square bg-[var(--color-surface-container-low)] flex items-center justify-center">
                {signed[a.id] && a.mime_type?.startsWith("image/") ? (
                  <img src={signed[a.id]} alt={a.alt_text ?? a.file_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="material-symbols-outlined text-4xl text-[var(--color-on-surface-variant)]">description</span>
                )}
              </div>
              <div className="p-3">
                <div className="text-xs font-mono truncate" title={a.file_name}>{a.file_name}</div>
                <div className="text-xs text-[var(--color-on-surface-variant)] mt-1">
                  {a.file_size_bytes ? `${Math.round((a.file_size_bytes / 1024) * 10) / 10} KB` : ""}
                </div>
                <div className="flex gap-2 mt-2 text-xs">
                  {signed[a.id] && (
                    <>
                      <a href={signed[a.id]} target="_blank" rel="noreferrer" className="underline text-[var(--color-brand-purple)]">Ver</a>
                      <button onClick={() => copyUrl(a.id)} className="underline">Copiar URL</button>
                    </>
                  )}
                  <button onClick={() => remove(a)} className="text-red-600 ml-auto">Eliminar</button>
                </div>
              </div>
            </div>
          ))}
          {childAssets.length === 0 && childFolders.length === 0 && (
            <div className="col-span-full text-center py-12 text-[var(--color-on-surface-variant)]">
              Esta carpeta está vacía. Crea subcarpetas o sube archivos.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
